import { useState } from 'react'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import { FaFile } from 'react-icons/fa'
import { FiBell } from 'react-icons/fi'
import { BiFilterAlt } from 'react-icons/bi'
import { BsCameraFill, BsPerson } from 'react-icons/bs'
import { MdOutlineImage, MdOutlineImageNotSupported } from 'react-icons/md'
import { SiCodeforces } from 'react-icons/si'

import { Pane, Dialog, Tooltip, Table } from 'evergreen-ui'

import { Link } from 'react-router-dom'

import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'

import { pl } from 'date-fns/locale'
import { Line } from 'react-chartjs-2'
import dateConvert from '../../../utils/formatDate'
import useApi from '../../../hooks/useApi'
import useTenantType from '../../../hooks/useTenantType'

import { fromConsumptionApi } from '../../../utils/Adapters/ConsumptionApiAdapter'
import { fromMeasurementApi } from '../../../utils/Adapters/MeasurementApiAdapter'

const returnConsumptionValuesForChart = (consumptions) => {
    if (Object.values(consumptions).length > 0) {
        return (
            Object.values(consumptions)
                .map((consumption) => {
                    return {
                        x: consumption?.consumptionTimestamp,
                        y: consumption?.consumption
                    }
                })
                // TODO: ensure that the data is sorted by date; check later on why Chart.js is not sorting it automatically
                .sort((a, b) => new Date(a.x) - new Date(b.x))
        )
    }
    return {}
}

const returnMeasurementsValuesForChart = (measurements) => {
    if (Object.values(measurements).length > 0) {
        return Object.values(measurements).map((measurement) => {
            return {
                x: measurement?.createdAt,
                y: measurement?.value
            }
        })
    }
    return {}
}

const MeterExpandedRow = ({ row, isExpanded }) => {
    const [isImageShown, setIsImageShown] = useState(false)
    const [formatUnit, setFormatUnit] = useState('day')
    const [consumptions, setConsumptions] = useState([])
    const [measurements, setMeasurements] = useState([])

    const chartType = row.defaultChart

    const isModerator = useSelector((state) => state.profile.profile.isModerator)

    const user = useSelector((state) => state.user.users[row.tenantMember])
    const apiKey = useSelector((state) => state.profile.profile.apiKey)

    const { isInstitutionalMonitoring } = useTenantType()

    const getUrl = () => {
        if (chartType === 'usage') {
            // set after date to 1 month before
            const afterDate = format(new Date().setMonth(new Date().getMonth() - 1), 'yyyy-MM-dd')
            const beforeDate = format(new Date().setDate(new Date().getDate()), 'yyyy-MM-dd')

            return `/app/meters${isModerator ? '/moderator' : ''}/usage/daily/?meter=${
                row.pk
            }&consumption_ts_after=${afterDate}&consumption_ts_before=${beforeDate}`
        }

        if (chartType === 'measurement') {
            if (isModerator) return `/app/meters/moderator/iot/measurement/?meter=${row.pk}`
            return `/app/meters/iot/measurement/?meter=${row.pk}`
        }

        throw new Error('Neither usage nor measurement is a default chart')
    }

    useApi({
        url: getUrl(),
        method: 'GET',
        apiKey,
        fromApiAdapter: chartType === 'measurement' ? fromMeasurementApi : fromConsumptionApi,
        queryName: [
            chartType === 'measurement' ? 'measurementByMeterId' : 'consumptionByMeterId',
            [row.pk]
        ],
        enabled: isExpanded,
        keepPreviousData: true,
        onSuccess: (data) =>
            chartType === 'measurement'
                ? setMeasurements(data.measurements)
                : setConsumptions(data.consumption)
    })

    const renderMeterType = () => {
        switch (row.type) {
            case 'cold_water':
                return 'Woda zimna'
            case 'hot_water':
                return 'Woda ciepła'
            case 'garden_water':
                return 'Woda ogrodowa'
            case 'gas':
                return 'Gaz'
            case 'electricity':
                return 'Elektryczność'
            case 'temperature':
                return 'Temperatura'
            case 'heat':
                return 'Ciepło'
            case 'temp_cw_in':
                return 'Tzw'
            case 'temp_dhw':
                return 'Tcwu'
            case 'temp_circ':
                return 'Tcyrk'
            case 'temp_ch_supply':
                return 'Temperatura zasilania CO'
            case 'temp_ch_return':
                return 'Temperatura powrotu CO'
            case 'temp_boiler_out':
                return 'Temperatura wyjścia z kotła'
            default:
                return 'Niezdefiniowany'
        }
    }

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: 'rgb(255, 255, 255)'
                }
            },
            title: {
                display: true,
                text: renderMeterType(),
                color: 'rgb(255, 255, 255)'
            },
            tooltip: {
                callbacks: {
                    label: (value) => ` ${value.parsed.y} ${row.unit || ''}`
                }
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        scales: {
            x: {
                type: 'time',
                adapters: {
                    date: {
                        locale: pl
                    }
                },
                time: {
                    unit: formatUnit,
                    tooltipFormat: 'dd MMM yyyy HH:mm'
                },
                ticks: {
                    color: 'rgb(255, 255, 255)'
                },
                grid: {
                    color: 'rgb(255, 255, 255, 0.4)'
                }
            },
            y: {
                ticks: {
                    color: 'rgb(255, 255, 255, 1)'
                },
                grid: {
                    color: 'rgb(255, 255, 255, 0.4)'
                },
                title: {
                    display: true,
                    text: `Zużycie [${row.unit}]`,
                    color: 'rgb(255, 255, 255, 1)'
                }
            }
        }
    }

    const data = {
        datasets: [
            {
                label: chartType === 'measurement' ? 'Wartość odczytu' : 'Wartość zużycia',
                data:
                    chartType === 'measurement'
                        ? returnMeasurementsValuesForChart(measurements, row)
                        : returnConsumptionValuesForChart(consumptions, row),
                borderColor: 'rgb(242, 154, 74)',
                backgroundColor: 'rgb(255, 255, 255)',
                tension: 0.1
            }
        ]
    }

    const handleShowImageClick = () => {
        setIsImageShown(true)
    }

    const renderImageButton = () => {
        const renderName = () => {
            if (row.serialNumber) return row.serialNumber
            if (!row.serialNumber && row.image) return 'Zdjęcie'
            return 'Brak'
        }

        if (row.image)
            return [<MdOutlineImage size="18" fill="lightGreen" key="image" />, renderName()]
        return [<MdOutlineImageNotSupported size="18" fill="orange" key="image" />, renderName()]
    }

    const renderProperty = (element) => {
        let returnValue = row?.[element] !== '' ? row?.[element] : user?.[element]
        if (!returnValue) returnValue = '-'

        return returnValue
    }

    return (
        <div className={`more_info ${isExpanded ? 'expanded' : 'hidden'}`}>
            <div className="more_info_content">
                <div className="column">
                    <p id="title">Szczegóły licznika </p>
                    <div className="data_grid">
                        {isModerator && (
                            <>
                                <p>Typ odbiorcy:</p>
                                <p>{user?.isCompany ? 'Instytucjonalny' : 'Indywidualny'}</p>
                            </>
                        )}
                        <p>Data utworzenia:</p>
                        <p>{dateConvert(row.createdAt)}</p>
                        <p>Typ licznika:</p>
                        <p>{renderMeterType()}</p>
                        <p>Jednostka:</p>
                        <p>{renderProperty('unit')}</p>
                        <p className="inLine">
                            {row.type === 'hot_water' || row.type === 'cold_water'
                                ? 'Nr nakładki:'
                                : 'Nr modułu:'}
                        </p>
                        <p className="inLine">
                            {row.radioId ? (
                                <button type="button" className="clearButton" disabled>
                                    {row.radioId}
                                </button>
                            ) : (
                                '-'
                            )}
                        </p>
                        <p className="inLine">Nr Licznika:</p>
                        <p className="inLine">
                            <Tooltip
                                content="Pokaż zdjęcie licznika"
                                showDelay={500}
                                position="right"
                            >
                                <button
                                    type="button"
                                    className="clearButton"
                                    onClick={handleShowImageClick}
                                    disabled={!row.image}
                                >
                                    {renderImageButton()}
                                </button>
                            </Tooltip>
                        </p>
                        <p id="spacer_row">Adres licznika:</p>
                        <p style={{ textIndent: '1em' }}>Ulica:</p>
                        <p>{renderProperty('street')}</p>
                        <p style={{ textIndent: '1em' }}>Miasto:</p>
                        <p>{renderProperty('city')}</p>
                        <p style={{ textIndent: '1em' }}>Kod pocztowy:</p>
                        <p>{renderProperty('zipCode')}</p>
                    </div>
                </div>
                <div className="more_info_content_chart">
                    <Line options={options} data={data} color="white" />
                </div>

                <div className="column">
                    <div className="column_right">
                        <p id="title">Dostępne działania </p>
                        {!isInstitutionalMonitoring && (
                            <Tooltip
                                content={
                                    isInstitutionalMonitoring
                                        ? 'Pokaż urządzenia tej lokalizacji'
                                        : 'Pokaż liczniki tego odbiorcy'
                                }
                                showDelay={500}
                                position="left"
                            >
                                <Link to={`/meters?user_id=${row.tenantMember}`}>
                                    <button type="button" className="button">
                                        <BiFilterAlt size="18" />{' '}
                                        {isInstitutionalMonitoring
                                            ? 'Filtruj po lokalizacji'
                                            : 'Filtruj po odbiorcy'}
                                    </button>
                                </Link>
                            </Tooltip>
                        )}
                        <Tooltip content="Przejdź do powiadomień" showDelay={500} position="left">
                            <Link
                                to={`/notifications?user_id=${row.tenantMember}&meter_id=${row.pk}`}
                            >
                                <button type="button" className="button">
                                    <FiBell size="18" color="#142c66" /> Powiadomienia
                                </button>
                            </Link>
                        </Tooltip>
                        {!isInstitutionalMonitoring && (
                            <Tooltip
                                content="Przejdź do dokumentów"
                                showDelay={500}
                                position="left"
                            >
                                <Link to={`/documents?meter_id=${row.pk}`}>
                                    <button type="button" className="button">
                                        <FaFile size="18" color="#142c66" /> Dokumenty
                                    </button>
                                </Link>
                            </Tooltip>
                        )}
                    </div>
                    <div className="column_right">
                        <Tooltip
                            content={
                                isInstitutionalMonitoring
                                    ? 'Przejdź do lokalizacji'
                                    : 'Przejdź do odbiorcy'
                            }
                            showDelay={500}
                            position="left"
                        >
                            <Link to={`/users?user_id=${row.tenantMember}`}>
                                <button type="button" className="button">
                                    <BsPerson size="18" color="#142c66" />{' '}
                                    {isInstitutionalMonitoring ? 'Lokalizacja' : 'Odbiorca'}
                                </button>
                            </Link>
                        </Tooltip>
                        <Tooltip
                            content={
                                isInstitutionalMonitoring
                                    ? 'Przejdź do pomiarów'
                                    : 'Przejdź do odczytów'
                            }
                            showDelay={500}
                            position="left"
                        >
                            <Link to={`/readings?meter_id=${row.pk}`}>
                                <button type="button" className="button">
                                    <BsCameraFill size="18" color="#142c66" />{' '}
                                    {isInstitutionalMonitoring ? 'Pomiary' : 'Odczyty'}
                                </button>
                            </Link>
                        </Tooltip>
                        {!isInstitutionalMonitoring && (
                            <Tooltip content="Przejdź do zużycia" showDelay={500} position="left">
                                <Link to={`/consumption?meter_id=${row.pk}`}>
                                    <button type="button" className="button">
                                        <SiCodeforces size="18" color="#142c66" /> Zużycie
                                    </button>
                                </Link>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
            <Pane>
                <Dialog
                    isShown={isImageShown}
                    title="Zdjęcie licznika"
                    onCloseComplete={() => setIsImageShown(false)}
                    hasFooter={false}
                >
                    <Zoom>
                        <div className="dialog_image_container">
                            <img src={row.image} alt="meter" />
                        </div>
                    </Zoom>
                </Dialog>
            </Pane>
        </div>
    )
}

export default MeterExpandedRow
